import { useCan } from '@features/auth';
import { ClientSelector } from '@features/catalog';
import { useClients, type ClientTarget } from '@features/clients';
import {
    Button,
    Card,
    CardHeader,
    Chip,
    ErrorState,
    LoadingRows,
    TextField,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useMemo, useState } from 'react';

import { useStartDryRun } from '../hooks/use-bulk-migrations';
import { MAX_LAUNCH_TENANTS } from '../model/selection';

import type { BulkModule, JobHeader } from '../schemas/bulk-migration';

/**
 * Step 2: analyse — a dry-run over every client, or over the ones chosen.
 *
 * It writes nothing in any tenant, but it reads every org's Salesforce and
 * queues one message per client on the runner, so it is `engineer`, like a
 * release operation over all clients. A reader sees the history and no button.
 */
export function AnalyzePanel({
    target,
    module,
    onStarted,
}: {
    target: ClientTarget;
    module: BulkModule;
    onStarted: (job: JobHeader) => void;
}) {
    const canAnalyze = useCan('engineer');
    const start = useStartDryRun(target, module.moduleId);
    const [scope, setScope] = useState<'all' | 'chosen'>('all');
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [excludeSandboxes, setExcludeSandboxes] = useState(true);
    const [reason, setReason] = useState('');

    const blocked =
        scope === 'chosen' && (selected.length === 0 || selected.length > MAX_LAUNCH_TENANTS);

    const submit = () => {
        start.mutate(
            {
                excludeSandboxes,
                ...(scope === 'chosen' ? { tenantIds: selected } : {}),
                ...(reason.trim() ? { reason: reason.trim() } : {}),
            },
            { onSuccess: onStarted },
        );
    };

    return (
        <Card>
            <CardHeader
                title={`Analyze ${module.title}`}
                subtitle="Reads Salesforce and shows what would be migrated. Writes nothing."
            />
            <div className="flex flex-col gap-4 px-5 py-4">
                <div className="flex gap-2">
                    <Chip
                        active={scope === 'all'}
                        onClick={() => {
                            setScope('all');
                        }}
                    >
                        All clients
                    </Chip>
                    <Chip
                        active={scope === 'chosen'}
                        onClick={() => {
                            setScope('chosen');
                        }}
                    >
                        Choose clients
                    </Chip>
                </div>

                {scope === 'chosen' && (
                    <ChosenClients
                        target={target}
                        selected={selected}
                        onChange={setSelected}
                        disabled={!canAnalyze || start.isPending}
                    />
                )}

                <label className="flex items-center gap-3 text-[13px] text-content-body">
                    <Toggle
                        on={excludeSandboxes}
                        onChange={setExcludeSandboxes}
                        disabled={!canAnalyze || start.isPending}
                        aria-label="Exclude sandboxes"
                    />
                    Exclude sandboxes
                </label>

                <TextField
                    label="Note (optional)"
                    value={reason}
                    onChange={setReason}
                    placeholder="Why this analysis — shown in the history"
                    disabled={!canAnalyze || start.isPending}
                />

                {start.error && <WriteError error={start.error} />}

                <div className="flex items-center gap-3">
                    {canAnalyze ? (
                        <Button onClick={submit} disabled={start.isPending || blocked}>
                            {start.isPending ? 'Starting…' : 'Analyze'}
                        </Button>
                    ) : (
                        <p className="text-[12.5px] text-content-muted">
                            Analyzing needs the engineer role. You can open past analyses below.
                        </p>
                    )}
                    {scope === 'chosen' && (
                        <span className="text-[12.5px] text-content-muted">
                            {selected.length} selected
                        </span>
                    )}
                </div>
            </div>
        </Card>
    );
}

/**
 * The client list is read only once "Choose clients" is picked: analysing every
 * client needs no list, and a backend without tenants-api configured should not
 * fail the screen for a picker nobody opened.
 */
function ChosenClients({
    target,
    selected,
    onChange,
    disabled,
}: {
    target: ClientTarget;
    selected: readonly string[];
    onChange: (next: readonly string[]) => void;
    disabled: boolean;
}) {
    const clients = useClients(target);
    const options = useMemo(() => clients.data ?? [], [clients.data]);

    if (clients.isPending) return <LoadingRows label="Loading clients…" />;
    if (clients.isError)
        return <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />;
    return (
        <ClientSelector
            clients={options}
            selected={selected}
            onChange={onChange}
            disabled={disabled}
        />
    );
}
